import { useState, useEffect, Component, ReactNode } from "react";
import { SidebarProvider } from "@/components/ui/sidebar";
import OwnerSidebar from "./OwnerSidebar";
import OwnerHeader from "./OwnerHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { db, auth } from "../firebase/config";
import { collection, getDocs, getDoc, doc, updateDoc, setDoc, arrayUnion, arrayRemove, Timestamp } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import ContentLoader from "../authentication/ContentLoader";

// Error Boundary Component
class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; error: string | null }> {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error: error.message };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("ErrorBoundary caught error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Card className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
          <CardHeader>
            <CardTitle>Something went wrong</CardTitle>
            <CardDescription>
              An error occurred: {this.state.error || "Unknown error"}. Please try refreshing the page or contact support.
            </CardDescription>
          </CardHeader>
        </Card>
      );
    }
    return this.props.children;
  }
}

// Define interfaces
interface Affiliation {
  role: string;
  schoolID?: string;
}

interface School {
  id: string;
  name: string;
}

interface PaymentMethod {
  id: string;
  provider: string;
  details: any;
  addedAt: Date | Timestamp;
  addedBy: string;
}

// Zod schemas for each provider
const mpesaSchema = z.object({
  paybillNumber: z.string().min(1, "Paybill/Till Number is required"),
  accountReference: z.string().min(1, "Account Reference is required"),
  passkey: z.string().min(1, "Passkey is required"),
  consumerKey: z.string().min(1, "Consumer Key is required"),
  consumerSecret: z.string().min(1, "Consumer Secret is required"),
  callbackUrl: z.string().url("Valid URL required for callbacks").optional(),
  initiatorName: z.string().min(1, "Initiator Name is required"),
  securityCredential: z.string().min(1, "Security Credential is required"),
});

// Equity/Jenga credentials differ by portal wording: some portals use consumerKey/consumerSecret
// Instead of rejecting, accept both clientId/clientSecret and consumerKey/consumerSecret.
// In sandbox, allow empty fields for quick testing; in production, enforce required keys.
const equitySchema = z
  .object({
    merchantCode: z.string().optional(),
    apiKey: z.string().optional(),
    clientId: z.string().optional(),
    clientSecret: z.string().optional(),
    consumerKey: z.string().optional(),
    consumerSecret: z.string().optional(),
    accountNumber: z.string().optional(),
    countryCode: z.string().min(2).max(3, "Country Code (e.g., KE, UG)").optional(),
    privateKey: z.string().optional(),
    publicKey: z.string().optional(),
  baseUrl: z.string().url("Base URL (sandbox/production)").optional(),
  environment: z.enum(["sandbox", "production"]),
    callbackUrl: z.string().url("Callback/Webhook URL is required").optional(),
  })
  .superRefine((val, ctx) => {
    // In sandbox, relax validation entirely
    if (val.environment === "sandbox") {
      return;
    }

    // Production: enforce required fields
    const missing: string[] = [];
    const hasClientPair = !!(val.clientId && val.clientSecret);
    const hasConsumerPair = !!(val.consumerKey && val.consumerSecret);
    if (!hasClientPair && !hasConsumerPair) {
      missing.push("clientId/clientSecret OR consumerKey/consumerSecret");
    }
    if (!val.apiKey) missing.push("apiKey");
    if (!val.accountNumber) missing.push("accountNumber");
    if (!val.countryCode) missing.push("countryCode");
    if (!val.baseUrl) missing.push("baseUrl");
    if (!val.callbackUrl) missing.push("callbackUrl");
    // Private/public keys are often needed for signing in some flows; enforce in production
    if (!val.privateKey) missing.push("privateKey");
    if (!val.publicKey) missing.push("publicKey");

    if (missing.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Missing required fields for production: ${missing.join(", ")}`,
        path: ["environment"],
      });
    }
  });

// KCB/Buni: allow sandbox to be lenient, production requires proper setup
const kcbSchema = z
  .object({
    clientId: z.string().optional(),
    clientSecret: z.string().optional(),
    consumerKey: z.string().optional(),
    consumerSecret: z.string().optional(),
    apiKey: z.string().optional(),
    preissuedToken: z.string().optional(),
    baseUrl: z.string().url("Base URL (Buni)").optional(),
    tillNumber: z.string().optional(),
    accountNumber: z.string().optional(),
    branchCode: z.string().optional(),
    countryCode: z.string().min(2).max(3, "Country Code (e.g., KE)").optional(),
    webhookUrl: z.string().url("Valid URL required for webhooks").optional(),
    environment: z.enum(["sandbox", "production"]),
  })
  .superRefine((val, ctx) => {
    if (val.environment === "sandbox") return;
    const missing: string[] = [];
    const hasClientPair = !!(val.clientId && val.clientSecret);
    const hasConsumerPair = !!(val.consumerKey && val.consumerSecret);
    if (!hasClientPair && !hasConsumerPair) missing.push("clientId/clientSecret OR consumerKey/consumerSecret");
    if (!val.apiKey) missing.push("apiKey");
    if (!val.baseUrl) missing.push("baseUrl");
    if (!val.accountNumber) missing.push("accountNumber");
    if (!val.countryCode) missing.push("countryCode");
    if (missing.length) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Missing required fields for production: ${missing.join(", ")}`, path: ["environment"] });
    }
  });

// Telkom Kenya T-Kash schema
const telkomSchema = z
  .object({
    merchantCode: z.string().optional(),
    apiKey: z.string().optional(),
    clientId: z.string().optional(),
    clientSecret: z.string().optional(),
    accountNumber: z.string().optional(),
    baseUrl: z.string().url("Base URL (sandbox/production)").optional(),
    environment: z.enum(["sandbox", "production"]),
    callbackUrl: z.string().url("Callback/Webhook URL is required").optional(),
  })
  .superRefine((val, ctx) => {
    if (val.environment === "sandbox") return;
    const missing: string[] = [];
    if (!val.clientId) missing.push("clientId");
    if (!val.clientSecret) missing.push("clientSecret");
    if (!val.apiKey) missing.push("apiKey");
    if (!val.accountNumber) missing.push("accountNumber");
    if (!val.baseUrl) missing.push("baseUrl");
    if (!val.callbackUrl) missing.push("callbackUrl");
    if (missing.length) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Missing required fields for production: ${missing.join(", ")}`, path: ["environment"] });
    }
  });

// Airtel Money schema
const airtelSchema = z
  .object({
    merchantId: z.string().optional(),
    apiKey: z.string().optional(),
    clientId: z.string().optional(),
    clientSecret: z.string().optional(),
    // Airtel merchant balance is for the authenticated wallet; accountNumber not strictly required
    accountNumber: z.string().optional(),
    countryCode: z.string().min(2).max(3, "Country Code (e.g., KE)").optional(),
    currency: z.string().min(3).max(3, "Currency (e.g., KES)").optional(),
    baseUrl: z.string().url("Base URL (sandbox/production)").optional(),
    environment: z.enum(["sandbox", "production"]),
    callbackUrl: z.string().url("Callback/Webhook URL is required").optional(),
  })
  .superRefine((val, ctx) => {
    if (val.environment === "sandbox") return;
    const missing: string[] = [];
    if (!val.clientId) missing.push("clientId");
    if (!val.clientSecret) missing.push("clientSecret");
    if (!val.apiKey) missing.push("apiKey");
    if (!val.countryCode) missing.push("countryCode");
    if (!val.currency) missing.push("currency");
    if (!val.baseUrl) missing.push("baseUrl");
    if (!val.callbackUrl) missing.push("callbackUrl");
    if (missing.length) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Missing required fields for production: ${missing.join(", ")}`, path: ["environment"] });
    }
  });

// Standard Chartered Bank schema
const standardCharteredSchema = z
  .object({
    clientId: z.string().optional(),
    clientSecret: z.string().optional(),
    apiKey: z.string().optional(),
    accountNumber: z.string().optional(),
    branchCode: z.string().optional(),
    baseUrl: z.string().url("Base URL (sandbox/production)").optional(),
    environment: z.enum(["sandbox", "production"]),
    callbackUrl: z.string().url("Callback/Webhook URL is required").optional(),
  })
  .superRefine((val, ctx) => {
    if (val.environment === "sandbox") return;
    const missing: string[] = [];
    if (!val.clientId) missing.push("clientId");
    if (!val.clientSecret) missing.push("clientSecret");
    if (!val.apiKey) missing.push("apiKey");
    if (!val.accountNumber) missing.push("accountNumber");
    if (!val.baseUrl) missing.push("baseUrl");
    if (!val.callbackUrl) missing.push("callbackUrl");
    if (missing.length) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Missing required fields for production: ${missing.join(", ")}`, path: ["environment"] });
    }
  });

// Family Bank schema
const familyBankSchema = z
  .object({
    clientId: z.string().optional(),
    clientSecret: z.string().optional(),
    apiKey: z.string().optional(),
    accountNumber: z.string().optional(),
    branchCode: z.string().optional(),
    baseUrl: z.string().url("Base URL (sandbox/production)").optional(),
    environment: z.enum(["sandbox", "production"]),
    callbackUrl: z.string().url("Callback/Webhook URL is required").optional(),
  })
  .superRefine((val, ctx) => {
    if (val.environment === "sandbox") return;
    const missing: string[] = [];
    if (!val.clientId) missing.push("clientId");
    if (!val.clientSecret) missing.push("clientSecret");
    if (!val.apiKey) missing.push("apiKey");
    if (!val.accountNumber) missing.push("accountNumber");
    if (!val.baseUrl) missing.push("baseUrl");
    if (!val.callbackUrl) missing.push("callbackUrl");
    if (missing.length) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Missing required fields for production: ${missing.join(", ")}`, path: ["environment"] });
    }
  });

type MpesaFormData = z.infer<typeof mpesaSchema>;
type EquityFormData = z.infer<typeof equitySchema>;
type KcbFormData = z.infer<typeof kcbSchema>;
type TelkomFormData = z.infer<typeof telkomSchema>;
type AirtelFormData = z.infer<typeof airtelSchema>;
type StandardCharteredFormData = z.infer<typeof standardCharteredSchema>;
type FamilyBankFormData = z.infer<typeof familyBankSchema>;

const pesapalIndependentSchema = z.object({
  consumerKey: z.string().min(1, "Consumer Key is required"),
  consumerSecret: z.string().min(1, "Consumer Secret is required"),
});
type PesapalIndependentFormData = z.infer<typeof pesapalIndependentSchema>;

const OwnerPayments = () => {
  const [activeSection, setActiveSection] = useState("Payments");
  const [schools, setSchools] = useState<School[]>([]);
  const [selectedSchool, setSelectedSchool] = useState<string>("");
  const [selectedProvider, setSelectedProvider] = useState<string>("");
  const [isLoadingSchools, setIsLoadingSchools] = useState(true);
  const [isOwner, setIsOwner] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingMethodId, setEditingMethodId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mpesaForm = useForm<MpesaFormData>({
    resolver: zodResolver(mpesaSchema),
    defaultValues: {
      paybillNumber: "",
      accountReference: "",
      passkey: "",
      consumerKey: "",
      consumerSecret: "",
      initiatorName: "",
      securityCredential: "",
      callbackUrl: "https://us-central1-cbsms-da9ce.cloudfunctions.net/schooladminMpesaCallback",
    },
  });

  const equityForm = useForm<EquityFormData>({
    resolver: zodResolver(equitySchema),
    defaultValues: {
      merchantCode: "",
      apiKey: "",
      clientId: "",
      clientSecret: "",
      consumerKey: "",
      consumerSecret: "",
      accountNumber: "",
      countryCode: "KE",
      privateKey: "",
      publicKey: "",
      baseUrl: "https://uat.finserve.africa",
      environment: "sandbox",
      callbackUrl: "https://us-central1-cbsms-da9ce.cloudfunctions.net/equityWebhook",
    },
  });

  const kcbForm = useForm<KcbFormData>({
    resolver: zodResolver(kcbSchema),
    defaultValues: {
      clientId: "",
      clientSecret: "",
      consumerKey: "",
      consumerSecret: "",
      apiKey: "",
      baseUrl: "https://sandbox.buni.kcbgroup.com",
      tillNumber: "",
      accountNumber: "",
      branchCode: "",
      countryCode: "KE",
      webhookUrl: "https://us-central1-cbsms-da9ce.cloudfunctions.net/kcbBuniCallback",
      environment: "sandbox",
    },
  });

  const telkomForm = useForm<TelkomFormData>({
    resolver: zodResolver(telkomSchema),
    defaultValues: {
      merchantCode: "",
      apiKey: "",
      clientId: "",
      clientSecret: "",
      accountNumber: "",
      baseUrl: "https://sandbox.tkash.telkom.co.ke",
      environment: "sandbox",
      callbackUrl: "https://us-central1-cbsms-da9ce.cloudfunctions.net/telkomCallback",
    },
  });

  const airtelForm = useForm<AirtelFormData>({
    resolver: zodResolver(airtelSchema),
    defaultValues: {
      merchantId: "",
      apiKey: "",
      clientId: "",
      clientSecret: "",
      accountNumber: "",
      countryCode: "KE",
      currency: "KES",
      baseUrl: "https://openapiuat.airtel.africa",
      environment: "sandbox",
      callbackUrl: "https://us-central1-cbsms-da9ce.cloudfunctions.net/airtelCallback",
    },
  });

  const standardCharteredForm = useForm<StandardCharteredFormData>({
    resolver: zodResolver(standardCharteredSchema),
    defaultValues: {
      clientId: "",
      clientSecret: "",
      apiKey: "",
      accountNumber: "",
      branchCode: "",
      baseUrl: "https://sandbox.sc.com/api",
      environment: "sandbox",
      callbackUrl: "https://us-central1-cbsms-da9ce.cloudfunctions.net/standardCharteredCallback",
    },
  });

  const familyBankForm = useForm<FamilyBankFormData>({
    resolver: zodResolver(familyBankSchema),
    defaultValues: {
      clientId: "",
      clientSecret: "",
      apiKey: "",
      accountNumber: "",
      branchCode: "",
      baseUrl: "https://sandbox-api.familybank.co.ke",
      environment: "sandbox",
      callbackUrl: "https://us-central1-cbsms-da9ce.cloudfunctions.net/familyBankCallback",
    },
  });

  useEffect(() => {
    console.log("OwnerPayments: Starting authentication check");
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        console.log("OwnerPayments: User authenticated, UID:", currentUser.uid);
        try {
          await currentUser.getIdToken(true);
          const idTokenResult = await currentUser.getIdTokenResult(true);
          if (!idTokenResult) {
            console.error("OwnerPayments: Failed to retrieve user token claims");
            setError("Failed to retrieve user token claims");
            setIsLoadingSchools(false);
            return;
          }

          const affiliations = (idTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
          const defaultAffiliation = idTokenResult.claims.defaultAffiliation as Affiliation | undefined;

          console.log("OwnerPayments: ID Token claims:", { affiliations, defaultAffiliation });

          let selectedAffiliation: Affiliation | null = null;
          if (affiliations.length > 0) {
            if (defaultAffiliation && defaultAffiliation.role === "owner") {
              selectedAffiliation = defaultAffiliation;
            } else {
              selectedAffiliation = affiliations.find(aff => aff.role === "owner") || defaultAffiliation || affiliations[0];
            }
          }

          if (selectedAffiliation?.role === "owner") {
            console.log("OwnerPayments: User is owner, fetching schools");
            setIsOwner(true);
            try {
              const schoolsCol = collection(db, "schools");
              const schoolsSnapshot = await getDocs(schoolsCol);
              console.log("OwnerPayments: Schools fetched, count:", schoolsSnapshot.size);
              if (schoolsSnapshot.empty) {
                console.warn("OwnerPayments: No schools found in Firestore");
                setError("No schools found. Please create a school first.");
                setIsLoadingSchools(false);
                return;
              }

              const schoolsPromises = schoolsSnapshot.docs.map(async (schoolDoc) => {
                const schoolID = schoolDoc.id;
                const detailsDoc = await getDoc(doc(db, "schools", schoolID, "schoolDetails", "main"));
                const name = detailsDoc.exists() ? detailsDoc.data()?.name || schoolID : schoolID;
                console.log("OwnerPayments: School fetched:", { schoolID, name });
                return { id: schoolID, name };
              });
              const schoolsData = await Promise.all(schoolsPromises);
              const sortedSchools = schoolsData.sort((a, b) => a.name.localeCompare(b.name));
              setSchools(sortedSchools);
              console.log("OwnerPayments: Schools set:", sortedSchools);
            } catch (err: any) {
              console.error("OwnerPayments: Error fetching schools:", err);
              setError(`Failed to load schools: ${err.message}`);
            }
          } else {
            console.warn("OwnerPayments: User is not owner, affiliations:", affiliations);
            setError("Owner privileges required to access this page.");
          }
        } catch (err: any) {
          console.error("OwnerPayments: Error loading user data:", err);
          setError(`Error loading user data: ${err.message}`);
        }
      } else {
        console.warn("OwnerPayments: No authenticated user");
        setError("Please sign in to access this page.");
      }
      setIsLoadingSchools(false);
    });

    return () => {
      console.log("OwnerPayments: Unsubscribing from auth state changes");
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    const fetchPaymentMethods = async () => {
      if (!selectedSchool) {
        console.log("OwnerPayments: No school selected, clearing payment methods");
        setPaymentMethods([]);
        return;
      }

      console.log("OwnerPayments: Fetching payment methods for school:", selectedSchool);
      try {
        const configDoc = await getDoc(doc(db, "schools", selectedSchool, "paymentConfig", "main"));
        if (configDoc.exists()) {
          const data = configDoc.data();
          const methods = data.paymentMethods || [];
          const methodsWithId = methods
            .filter((method: any) => method.id && method.provider && method.addedAt && method.addedBy)
            .map((method: any, index: number) => ({
              ...method,
              id: method.id || `method_${index}_${Date.now()}`,
              addedAt: method.addedAt instanceof Timestamp ? method.addedAt.toDate() : new Date(method.addedAt),
            }));
          console.log("OwnerPayments: Payment methods fetched:", methodsWithId);
          setPaymentMethods(methodsWithId);
        } else {
          console.log("OwnerPayments: No payment config found for school:", selectedSchool);
          setPaymentMethods([]);
        }
      } catch (err: any) {
        console.error("OwnerPayments: Error fetching payment methods:", err);
        setError(`Failed to load payment methods: ${err.message}`);
        setPaymentMethods([]);
      }
    };

    fetchPaymentMethods();
  }, [selectedSchool]);


  const handleSchoolSelect = (value: string) => {
    console.log("OwnerPayments: School selected:", value);
    setSelectedSchool(value);
    setSelectedProvider("");
    setIsEditing(false);
    setEditingMethodId(null);
    mpesaForm.reset();
    equityForm.reset();
    kcbForm.reset();
  };

  const handleProviderSelect = (value: string) => {
    console.log("OwnerPayments: Provider selected:", value);
    setSelectedProvider(value);
    setIsEditing(false);
    setEditingMethodId(null);
    if (value === "mpesa") {
      mpesaForm.reset({
        paybillNumber: "",
        accountReference: "",
        passkey: "",
        consumerKey: "",
        consumerSecret: "",
        callbackUrl: selectedSchool
          ? `https://us-central1-cbsms-da9ce.cloudfunctions.net/schooladminMpesaCallback?schoolId=${encodeURIComponent(selectedSchool)}`
          : "https://us-central1-cbsms-da9ce.cloudfunctions.net/schooladminMpesaCallback",
      });
    }
  if (value === "equity") equityForm.reset({
      merchantCode: "",
      apiKey: "",
      clientId: "",
      clientSecret: "",
      consumerKey: "",
      consumerSecret: "",
      accountNumber: "",
      countryCode: "KE",
      privateKey: "",
      publicKey: "",
  baseUrl: "https://uat.finserve.africa",
      environment: "sandbox",
      callbackUrl: selectedSchool
        ? `https://us-central1-cbsms-da9ce.cloudfunctions.net/equityWebhook?schoolId=${encodeURIComponent(selectedSchool)}`
        : "https://us-central1-cbsms-da9ce.cloudfunctions.net/equityWebhook",
    });
    if (value === "kcb")
      kcbForm.reset({
        clientId: "",
        clientSecret: "",
        consumerKey: "",
        consumerSecret: "",
        apiKey: "",
        preissuedToken: "",
        baseUrl: "https://sandbox.buni.kcbgroup.com",
        tillNumber: "",
        accountNumber: "",
        branchCode: "",
        countryCode: "KE",
        webhookUrl:
          selectedSchool
            ? `https://us-central1-cbsms-da9ce.cloudfunctions.net/kcbBuniCallback?schoolId=${encodeURIComponent(selectedSchool)}`
            : "https://us-central1-cbsms-da9ce.cloudfunctions.net/kcbBuniCallback",
        environment: "sandbox",
      });
  };

  const handleEdit = (method: PaymentMethod) => {
    console.log("OwnerPayments: Editing payment method:", method);
    setIsEditing(true);
    setEditingMethodId(method.id);
    setSelectedProvider(method.provider);

    const details = method.details;
    if (method.provider === "mpesa") {
      mpesaForm.reset({
        ...details,
        callbackUrl: selectedSchool
          ? `https://us-central1-cbsms-da9ce.cloudfunctions.net/schooladminMpesaCallback?schoolId=${encodeURIComponent(selectedSchool)}`
          : "https://us-central1-cbsms-da9ce.cloudfunctions.net/schooladminMpesaCallback",
      });
    } else if (method.provider === "equity") {
      equityForm.reset({
        merchantCode: details.merchantCode || "",
        apiKey: details.apiKey || "",
        clientId: details.clientId || "",
        clientSecret: details.clientSecret || "",
        consumerKey: details.consumerKey || "",
        consumerSecret: details.consumerSecret || "",
        accountNumber: details.accountNumber || "",
        countryCode: details.countryCode || "KE",
        privateKey: details.privateKey || "",
        publicKey: details.publicKey || "",
  baseUrl: details.baseUrl || "https://uat.finserve.africa",
        environment: details.environment || "sandbox",
        callbackUrl:
          details.callbackUrl ||
          (selectedSchool
            ? `https://us-central1-cbsms-da9ce.cloudfunctions.net/equityWebhook?schoolId=${encodeURIComponent(selectedSchool)}`
            : "https://us-central1-cbsms-da9ce.cloudfunctions.net/equityWebhook"),
      });
    } else if (method.provider === "kcb") {
      kcbForm.reset({
        clientId: details.clientId || "",
        clientSecret: details.clientSecret || "",
        consumerKey: details.consumerKey || "",
        consumerSecret: details.consumerSecret || "",
        apiKey: details.apiKey || details.subscriptionKey || "",
        preissuedToken: details.preissuedToken || "",
        baseUrl: details.baseUrl || "https://sandbox.buni.kcbgroup.com",
        tillNumber: details.tillNumber || "",
        accountNumber: details.accountNumber || "",
        branchCode: details.branchCode || "",
        countryCode: details.countryCode || "KE",
        webhookUrl:
          details.webhookUrl ||
          (selectedSchool
            ? `https://us-central1-cbsms-da9ce.cloudfunctions.net/kcbBuniCallback?schoolId=${encodeURIComponent(selectedSchool)}`
            : "https://us-central1-cbsms-da9ce.cloudfunctions.net/kcbBuniCallback"),
        environment: details.environment || "sandbox",
      });
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingMethodId(null);
    setSelectedProvider("");
    mpesaForm.reset();
    equityForm.reset();
    kcbForm.reset();
  };

  const onSubmit = async (data: MpesaFormData | EquityFormData | KcbFormData | TelkomFormData | AirtelFormData | StandardCharteredFormData | FamilyBankFormData) => {
    if (!selectedSchool || !selectedProvider) {
      console.warn("OwnerPayments: Submit attempted without school or provider");
      alert("Please select a school and provider.");
      return;
    }

    if (!auth.currentUser?.uid) {
      console.warn("OwnerPayments: No authenticated user for submit");
      alert("User not authenticated. Please log in again.");
      return;
    }

    console.log("OwnerPayments: Submitting payment method:", { school: selectedSchool, provider: selectedProvider, data });
    setIsSubmitting(true);
    try {
      const configDocRef = doc(db, "schools", selectedSchool, "paymentConfig", "main");
      const configDocSnap = await getDoc(configDocRef);

      if (!configDocSnap.exists()) {
        console.log("OwnerPayments: Creating new payment config document");
        await setDoc(configDocRef, { paymentMethods: [] });
      }

      const methodData = {
        id: isEditing && editingMethodId ? editingMethodId : `method_${Date.now()}`,
        provider: selectedProvider,
        details: selectedProvider === "mpesa"
          ? {
              ...(data as any),
              callbackUrl: selectedSchool
                ? `https://us-central1-cbsms-da9ce.cloudfunctions.net/schooladminMpesaCallback?schoolId=${encodeURIComponent(selectedSchool)}`
                : "https://us-central1-cbsms-da9ce.cloudfunctions.net/schooladminMpesaCallback",
            }
          : (data as any),
        addedAt: new Date(),
        addedBy: auth.currentUser.uid,
      };

      if (isEditing && editingMethodId) {
        const oldMethod = paymentMethods.find((m) => m.id === editingMethodId);
        if (!oldMethod) {
          console.error("OwnerPayments: Payment method to update not found:", editingMethodId);
          throw new Error("Payment method to update not found");
        }

        await updateDoc(configDocRef, {
          paymentMethods: arrayRemove(oldMethod),
        });
        await updateDoc(configDocRef, {
          paymentMethods: arrayUnion(methodData),
        });

        console.log("OwnerPayments: Payment method updated:", methodData);
        alert(`Payment method ${selectedProvider} updated successfully!`);
      } else {
        await updateDoc(configDocRef, {
          paymentMethods: arrayUnion(methodData),
        });

        console.log("OwnerPayments: Payment method added:", methodData);
        alert(`Payment method ${selectedProvider} added successfully!`);
      }

      setSelectedSchool("");
      setSelectedProvider("");
      setIsEditing(false);
      setEditingMethodId(null);
      mpesaForm.reset();
      equityForm.reset();
      kcbForm.reset();
      telkomForm.reset();
      airtelForm.reset();
      standardCharteredForm.reset();
      familyBankForm.reset();
      const fetchPaymentMethods = async () => {
        if (!selectedSchool) return;
        const configDoc = await getDoc(doc(db, "schools", selectedSchool, "paymentConfig", "main"));
        if (configDoc.exists()) {
          const data = configDoc.data();
          const methods = data.paymentMethods || [];
          const methodsWithId = methods
            .filter((method: any) => method.id && method.provider && method.addedAt && method.addedBy)
            .map((method: any, index: number) => ({
              ...method,
              id: method.id || `method_${index}_${Date.now()}`,
              addedAt: method.addedAt instanceof Timestamp ? method.addedAt.toDate() : new Date(method.addedAt),
            }));
          setPaymentMethods(methodsWithId);
          console.log("OwnerPayments: Payment methods refreshed:", methodsWithId);
        }
      };
      fetchPaymentMethods();
    } catch (err: any) {
      console.error("OwnerPayments: Error saving payment method:", err);
      alert(`Failed to save: ${err.message}`);
      setError(`Failed to save payment method: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <ErrorBoundary>
      <SidebarProvider>
        <div className="flex h-screen w-full bg-gray-50 text-gray-900 dark:bg-gray-800 dark:text-gray-100">
          <div className="w-48 sm:w-64 h-full border-r border-gray-200 dark:border-gray-700">
            <OwnerSidebar
              activeSection={activeSection}
              onSectionChange={setActiveSection}
              userRole="owner"
            />
          </div>
          <div className="flex flex-col flex-1 overflow-hidden">
            <OwnerHeader />
            <div className="flex-1 overflow-y-auto min-h-0 p-3 sm:p-6">
              <ContentLoader isLoading={isLoadingSchools} message="">
                  {!isOwner ? (
                    <Card className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
                      <CardHeader>
                        <CardTitle>Access Denied</CardTitle>
                        <CardDescription>
                          {error || "Owner privileges required."}
                        </CardDescription>
                      </CardHeader>
                    </Card>
                  ) : (
                <div className="space-y-8">
                {error && (
                  <Card className="bg-red-50 dark:bg-red-900 text-red-900 dark:text-red-100">
                    <CardHeader>
                      <CardTitle>Error</CardTitle>
                      <CardDescription>{error}</CardDescription>
                    </CardHeader>
                  </Card>
                )}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                  <div className="space-y-2">
                    <h1 className="text-3xl font-bold tracking-tight">Payment Providers</h1>
                    <p className="text-muted-foreground dark:text-gray-300">
                      Select a school and add/edit a payment provider (multiple supported; enables parent payments & real-time tracking).
                    </p>
                  </div>
                </div>

                <Card className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
                  <CardHeader>
                    <CardTitle>Configure Payment Provider</CardTitle>
                    <CardDescription>Select a school to manage its payment methods.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-4">
                      <Select value={selectedSchool} onValueChange={handleSchoolSelect}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a school" />
                        </SelectTrigger>
                        <SelectContent>
                          {schools.length === 0 ? (
                            <SelectItem value="" disabled>
                              No schools available
                            </SelectItem>
                          ) : (
                            schools.map((school) => (
                              <SelectItem key={school.id} value={school.id}>
                                {school.name}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      {selectedSchool && paymentMethods.length > 0 && (
                        <div className="space-y-2">
                          <Label>Existing Payment Methods</Label>
                          <div className="space-y-2 max-h-40 overflow-y-auto">
                            {paymentMethods.map((method) => (
                              <div key={method.id} className="flex justify-between items-center p-3 bg-gray-100 dark:bg-gray-700 rounded-md">
                                <div>
                                  <p className="font-medium">{method.provider.toUpperCase()}</p>
                                  <p className="text-xs text-muted-foreground dark:text-gray-300">
                                    Added: {(method.addedAt instanceof Date ? method.addedAt : new Date()).toLocaleDateString()}
                                  </p>
                                </div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleEdit(method)}
                                >
                                  Edit
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {selectedSchool && !isEditing && (
                        <Select value={selectedProvider} onValueChange={handleProviderSelect}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select payment provider to add" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="mpesa">M-Pesa</SelectItem>
                            <SelectItem value="equity">Equity Bank</SelectItem>
                            <SelectItem value="kcb">KCB Bank</SelectItem>
                            <SelectItem value="telkom">Telkom Kenya (T-Kash)</SelectItem>
                            <SelectItem value="airtel">Airtel Money</SelectItem>
                            <SelectItem value="standardChartered">Standard Chartered Bank</SelectItem>
                            <SelectItem value="familyBank">Family Bank</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                      {selectedProvider && selectedSchool && (
                        <>
                          {isEditing && (
                            <p className="text-sm text-blue-600 dark:text-blue-400">Editing existing {selectedProvider} method</p>
                          )}
                          {!isEditing && <p className="text-sm text-green-600 dark:text-green-400">Adding new {selectedProvider} method</p>}
                          {selectedProvider === "mpesa" && (
                            <Form {...mpesaForm}>
                              <form onSubmit={mpesaForm.handleSubmit(onSubmit)} className="space-y-4">
                                <FormField
                                  control={mpesaForm.control}
                                  name="paybillNumber"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Paybill/Till Number</FormLabel>
                                      <FormControl>
                                        <Input placeholder="e.g., 123456" {...field} />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={mpesaForm.control}
                                  name="accountReference"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Account Reference (default payment description)</FormLabel>
                                      <FormControl>
                                        <Input placeholder="e.g., School Fees" {...field} />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={mpesaForm.control}
                                  name="passkey"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Passkey (for STK Push)</FormLabel>
                                      <FormControl>
                                        <Input type="password" placeholder="Secure passkey" {...field} />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={mpesaForm.control}
                                  name="consumerKey"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Consumer Key</FormLabel>
                                      <FormControl>
                                        <Input placeholder="Consumer Key from Daraja Portal" {...field} />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={mpesaForm.control}
                                  name="consumerSecret"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Consumer Secret</FormLabel>
                                      <FormControl>
                                        <Input type="password" placeholder="Consumer Secret from Daraja Portal" {...field} />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                  <FormField
                                    control={mpesaForm.control}
                                    name="initiatorName"
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>Initiator Name</FormLabel>
                                        <FormControl>
                                          <Input placeholder="e.g., testapi (for sandbox)" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                  <FormField
                                    control={mpesaForm.control}
                                    name="securityCredential"
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>Security Credential (Encrypted)</FormLabel>
                                        <FormControl>
                                          <Input 
                                            type="password" 
                                            placeholder="Paste encrypted credential from Daraja Portal" 
                                            {...field} 
                                          />
                                        </FormControl>
                                        <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                          Generate this at: Daraja Portal → APIs → Account Balance → "Generate Security Credential" button
                                        </p>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                  Callback URL: https://us-central1-cbsms-da9ce.cloudfunctions.net/schooladminMpesaCallback (set automatically)
                                </p>
                                <Button type="submit" disabled={isSubmitting} className="w-full">
                                  {isSubmitting ? "Saving..." : isEditing ? "Update M-Pesa Method" : "Add M-Pesa Method"}
                                </Button>
                                {isEditing && (
                                  <Button type="button" variant="outline" onClick={handleCancelEdit} className="w-full mt-2">
                                    Cancel
                                  </Button>
                                )}
                              </form>
                            </Form>
                          )}
                          {selectedProvider === "equity" && (
                            <Form {...equityForm}>
                              <form onSubmit={equityForm.handleSubmit(onSubmit)} className="space-y-4">
                                <FormField
                                  control={equityForm.control}
                                  name="environment"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Environment</FormLabel>
                                      <Select
                                        value={field.value}
                                        onValueChange={(val) => {
                                          field.onChange(val);
                                          // Auto-set base URL when environment changes
                                          equityForm.setValue(
                                            "baseUrl",
                                            val === "production" ? "https://api.jengahq.io" : "https://uat.jengahq.io"
                                          );
                                        }}
                                      >
                                        <SelectTrigger>
                                          <SelectValue placeholder="Select environment" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="sandbox">Sandbox</SelectItem>
                                          <SelectItem value="production">Production</SelectItem>
                                        </SelectContent>
                                      </Select>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={equityForm.control}
                                  name="merchantCode"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Merchant Code</FormLabel>
                                      <FormControl>
                                        <Input placeholder="Merchant code from Equity" {...field} />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={equityForm.control}
                                  name="apiKey"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>API Key (Subscription Key)</FormLabel>
                                      <FormControl>
                                        <Input placeholder="API Key from Jenga portal" {...field} />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={equityForm.control}
                                  name="clientId"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Client ID</FormLabel>
                                      <FormControl>
                                        <Input placeholder="OAuth Client ID" {...field} />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={equityForm.control}
                                  name="clientSecret"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Client Secret</FormLabel>
                                      <FormControl>
                                        <Input type="password" placeholder="OAuth Client Secret" {...field} />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                  <FormField
                                    control={equityForm.control}
                                    name="consumerKey"
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>Consumer Key (alias of Client ID)</FormLabel>
                                        <FormControl>
                                          <Input placeholder="Consumer Key from Jenga portal" {...field} />
                                        </FormControl>
                                        <p className="text-xs text-muted-foreground dark:text-gray-300">If your portal shows Consumer Key/Secret, fill them here. One pair is enough.</p>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                  <FormField
                                    control={equityForm.control}
                                    name="consumerSecret"
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>Consumer Secret (alias of Client Secret)</FormLabel>
                                        <FormControl>
                                          <Input type="password" placeholder="Consumer Secret from Jenga portal" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                </div>
                                <FormField
                                  control={equityForm.control}
                                  name="accountNumber"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Account Number</FormLabel>
                                      <FormControl>
                                        <Input placeholder="Settlement account number" {...field} />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={equityForm.control}
                                  name="countryCode"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Country Code</FormLabel>
                                      <FormControl>
                                        <Input placeholder="KE, UG, TZ, etc." maxLength={3} {...field} />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={equityForm.control}
                                  name="privateKey"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Private Key (for signing requests)</FormLabel>
                                      <FormControl>
                                        <Input type="password" placeholder="Private key content or path" {...field} />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={equityForm.control}
                                  name="publicKey"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Public Key Certificate</FormLabel>
                                      <FormControl>
                                        <Input placeholder="Public certificate from Equity" {...field} />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={equityForm.control}
                                  name="baseUrl"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Base URL (Environment)</FormLabel>
                                      <FormControl>
                                        <Input placeholder="https://uat.jengahq.io or production URL" {...field} />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={equityForm.control}
                                  name="callbackUrl"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Callback/Webhook URL</FormLabel>
                                      <FormControl>
                                        <Input placeholder="https://yourapp.com/equity/webhook" {...field} />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <Button type="submit" disabled={isSubmitting} className="w-full">
                                  {isSubmitting ? "Saving..." : isEditing ? "Update Equity Method" : "Add Equity Method"}
                                </Button>
                              </form>
                            </Form>
                          )}
                          {selectedProvider === "kcb" && (
                            <Form {...kcbForm}>
                              <form onSubmit={kcbForm.handleSubmit(onSubmit)} className="space-y-4">
                                <FormField
                                  control={kcbForm.control}
                                  name="environment"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Environment</FormLabel>
                                      <Select
                                        value={field.value as any}
                                        onValueChange={(val) => {
                                          field.onChange(val);
                                          kcbForm.setValue(
                                            "baseUrl",
                                            val === "production" ? "https://api.buni.kcbgroup.com" : "https://sandbox.buni.kcbgroup.com"
                                          );
                                        }}
                                      >
                                        <SelectTrigger>
                                          <SelectValue placeholder="Select environment" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="sandbox">Sandbox</SelectItem>
                                          <SelectItem value="production">Production</SelectItem>
                                        </SelectContent>
                                      </Select>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={kcbForm.control}
                                  name="clientId"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Client ID</FormLabel>
                                      <FormControl>
                                        <Input placeholder="Client ID from KCB Buni Portal" {...field} />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={kcbForm.control}
                                  name="clientSecret"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Client Secret</FormLabel>
                                      <FormControl>
                                        <Input type="password" placeholder="Client Secret from KCB Buni Portal" {...field} />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                  <FormField
                                    control={kcbForm.control}
                                    name="consumerKey"
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>Consumer Key (alias of Client ID)</FormLabel>
                                        <FormControl>
                                          <Input placeholder="Optional alternate field name" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                  <FormField
                                    control={kcbForm.control}
                                    name="consumerSecret"
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>Consumer Secret (alias of Client Secret)</FormLabel>
                                        <FormControl>
                                          <Input type="password" placeholder="Optional alternate field name" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                </div>
                                <FormField
                                  control={kcbForm.control}
                                  name="apiKey"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>API Key (Subscription Key)</FormLabel>
                                      <FormControl>
                                        <Input placeholder="API Key from Buni Portal" {...field} />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={kcbForm.control}
                                  name="preissuedToken"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Pre-issued JWT Token (optional)</FormLabel>
                                      <FormControl>
                                        <Input placeholder="Paste token from 'Generate Key'" {...field} />
                                      </FormControl>
                                      <p className="text-xs text-muted-foreground dark:text-gray-300 mt-1">If provided, backend will skip OAuth and use this directly.</p>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={kcbForm.control}
                                  name="baseUrl"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Base URL</FormLabel>
                                      <FormControl>
                                        <Input placeholder="https://sandbox.buni.kcbgroup.com or production URL" {...field} />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={kcbForm.control}
                                  name="tillNumber"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Till Number</FormLabel>
                                      <FormControl>
                                        <Input placeholder="e.g., 123456" {...field} />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={kcbForm.control}
                                  name="accountNumber"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Settlement Account Number</FormLabel>
                                      <FormControl>
                                        <Input placeholder="e.g., 1234567890" {...field} />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={kcbForm.control}
                                  name="countryCode"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Country Code</FormLabel>
                                      <FormControl>
                                        <Input placeholder="KE" maxLength={3} {...field} />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField
                                  control={kcbForm.control}
                                  name="branchCode"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Branch Code</FormLabel>
                                      <FormControl>
                                        <Input placeholder="e.g., 001" {...field} />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                  Webhook URL: https://us-central1-cbsms-da9ce.cloudfunctions.net/kcbBuniCallback (set automatically)
                                </p>
                                <Button type="submit" disabled={isSubmitting} className="w-full">
                                  {isSubmitting ? "Saving..." : isEditing ? "Update KCB Method" : "Add KCB Method"}
                                </Button>
                              </form>
                            </Form>
                          )}

                          {/* Telkom Kenya T-Kash Form */}
                          {selectedProvider === "telkom" && (
                            <Form {...telkomForm}>
                              <form onSubmit={telkomForm.handleSubmit(onSubmit)} className="space-y-4">
                                <FormField
                                  control={telkomForm.control}
                                  name="environment"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Environment</FormLabel>
                                      <Select value={field.value as any} onValueChange={(val) => {
                                        field.onChange(val);
                                        telkomForm.setValue("baseUrl", val === "production" ? "https://api.tkash.telkom.co.ke" : "https://sandbox.tkash.telkom.co.ke");
                                      }}>
                                        <SelectTrigger><SelectValue placeholder="Select environment" /></SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="sandbox">Sandbox</SelectItem>
                                          <SelectItem value="production">Production</SelectItem>
                                        </SelectContent>
                                      </Select>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField control={telkomForm.control} name="clientId" render={({ field }) => (
                                  <FormItem><FormLabel>Client ID</FormLabel><FormControl><Input placeholder="Client ID from Telkom Portal" {...field} /></FormControl><FormMessage /></FormItem>
                                )} />
                                <FormField control={telkomForm.control} name="clientSecret" render={({ field }) => (
                                  <FormItem><FormLabel>Client Secret</FormLabel><FormControl><Input type="password" placeholder="Client Secret" {...field} /></FormControl><FormMessage /></FormItem>
                                )} />
                                <FormField control={telkomForm.control} name="apiKey" render={({ field }) => (
                                  <FormItem><FormLabel>API Key</FormLabel><FormControl><Input placeholder="API Key" {...field} /></FormControl><FormMessage /></FormItem>
                                )} />
                                <FormField control={telkomForm.control} name="accountNumber" render={({ field }) => (
                                  <FormItem><FormLabel>Account Number</FormLabel><FormControl><Input placeholder="T-Kash Account Number" {...field} /></FormControl><FormMessage /></FormItem>
                                )} />
                                <FormField control={telkomForm.control} name="merchantCode" render={({ field }) => (
                                  <FormItem><FormLabel>Merchant Code (Optional)</FormLabel><FormControl><Input placeholder="Merchant Code" {...field} /></FormControl><FormMessage /></FormItem>
                                )} />
                                <p className="text-sm text-gray-600 dark:text-gray-400">Callback URL: https://us-central1-cbsms-da9ce.cloudfunctions.net/telkomCallback (set automatically)</p>
                                <Button type="submit" disabled={isSubmitting} className="w-full">
                                  {isSubmitting ? "Saving..." : isEditing ? "Update Telkom Method" : "Add Telkom Method"}
                                </Button>
                              </form>
                            </Form>
                          )}

                          {/* Airtel Money Form */}
                          {selectedProvider === "airtel" && (
                            <Form {...airtelForm}>
                              <form onSubmit={airtelForm.handleSubmit(onSubmit)} className="space-y-4">
                                <FormField
                                  control={airtelForm.control}
                                  name="environment"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Environment</FormLabel>
                                      <Select value={field.value as any} onValueChange={(val) => {
                                        field.onChange(val);
                                        airtelForm.setValue("baseUrl", val === "production" ? "https://openapi.airtel.africa" : "https://openapiuat.airtel.africa");
                                      }}>
                                        <SelectTrigger><SelectValue placeholder="Select environment" /></SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="sandbox">Sandbox (UAT)</SelectItem>
                                          <SelectItem value="production">Production</SelectItem>
                                        </SelectContent>
                                      </Select>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField control={airtelForm.control} name="clientId" render={({ field }) => (
                                  <FormItem><FormLabel>Client ID</FormLabel><FormControl><Input placeholder="Client ID from Airtel Developer Portal" {...field} /></FormControl><FormMessage /></FormItem>
                                )} />
                                <FormField control={airtelForm.control} name="clientSecret" render={({ field }) => (
                                  <FormItem><FormLabel>Client Secret</FormLabel><FormControl><Input type="password" placeholder="Client Secret" {...field} /></FormControl><FormMessage /></FormItem>
                                )} />
                                <FormField control={airtelForm.control} name="apiKey" render={({ field }) => (
                                  <FormItem><FormLabel>API Key</FormLabel><FormControl><Input placeholder="API Key" {...field} /></FormControl><FormMessage /></FormItem>
                                )} />
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                  <FormField control={airtelForm.control} name="countryCode" render={({ field }) => (
                                    <FormItem><FormLabel>Country Code</FormLabel><FormControl><Input placeholder="KE" maxLength={3} {...field} /></FormControl><FormMessage /></FormItem>
                                  )} />
                                  <FormField control={airtelForm.control} name="currency" render={({ field }) => (
                                    <FormItem><FormLabel>Currency</FormLabel><FormControl><Input placeholder="KES" maxLength={3} {...field} /></FormControl><FormMessage /></FormItem>
                                  )} />
                                </div>
                                <FormField control={airtelForm.control} name="accountNumber" render={({ field }) => (
                                  <FormItem><FormLabel>Mobile Wallet Number (Optional)</FormLabel><FormControl><Input placeholder="Airtel Money Wallet Number" {...field} /></FormControl><FormMessage /></FormItem>
                                )} />
                                <FormField control={airtelForm.control} name="merchantId" render={({ field }) => (
                                  <FormItem><FormLabel>Merchant ID (Optional)</FormLabel><FormControl><Input placeholder="Merchant ID" {...field} /></FormControl><FormMessage /></FormItem>
                                )} />
                                <p className="text-sm text-gray-600 dark:text-gray-400">Callback URL: https://us-central1-cbsms-da9ce.cloudfunctions.net/airtelCallback (set automatically)</p>
                                <Button type="submit" disabled={isSubmitting} className="w-full">
                                  {isSubmitting ? "Saving..." : isEditing ? "Update Airtel Method" : "Add Airtel Method"}
                                </Button>
                              </form>
                            </Form>
                          )}

                          {/* Standard Chartered Bank Form */}
                          {selectedProvider === "standardChartered" && (
                            <Form {...standardCharteredForm}>
                              <form onSubmit={standardCharteredForm.handleSubmit(onSubmit)} className="space-y-4">
                                <FormField
                                  control={standardCharteredForm.control}
                                  name="environment"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Environment</FormLabel>
                                      <Select value={field.value as any} onValueChange={(val) => {
                                        field.onChange(val);
                                        standardCharteredForm.setValue("baseUrl", val === "production" ? "https://api.sc.com" : "https://sandbox.sc.com/api");
                                      }}>
                                        <SelectTrigger><SelectValue placeholder="Select environment" /></SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="sandbox">Sandbox</SelectItem>
                                          <SelectItem value="production">Production</SelectItem>
                                        </SelectContent>
                                      </Select>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField control={standardCharteredForm.control} name="clientId" render={({ field }) => (
                                  <FormItem><FormLabel>Client ID</FormLabel><FormControl><Input placeholder="Client ID from SC Developer Portal" {...field} /></FormControl><FormMessage /></FormItem>
                                )} />
                                <FormField control={standardCharteredForm.control} name="clientSecret" render={({ field }) => (
                                  <FormItem><FormLabel>Client Secret</FormLabel><FormControl><Input type="password" placeholder="Client Secret" {...field} /></FormControl><FormMessage /></FormItem>
                                )} />
                                <FormField control={standardCharteredForm.control} name="apiKey" render={({ field }) => (
                                  <FormItem><FormLabel>API Key</FormLabel><FormControl><Input placeholder="API Key" {...field} /></FormControl><FormMessage /></FormItem>
                                )} />
                                <FormField control={standardCharteredForm.control} name="accountNumber" render={({ field }) => (
                                  <FormItem><FormLabel>Account Number</FormLabel><FormControl><Input placeholder="SC Bank Account Number" {...field} /></FormControl><FormMessage /></FormItem>
                                )} />
                                <FormField control={standardCharteredForm.control} name="branchCode" render={({ field }) => (
                                  <FormItem><FormLabel>Branch Code (Optional)</FormLabel><FormControl><Input placeholder="Branch Code" {...field} /></FormControl><FormMessage /></FormItem>
                                )} />
                                <p className="text-sm text-gray-600 dark:text-gray-400">Callback URL: https://us-central1-cbsms-da9ce.cloudfunctions.net/standardCharteredCallback (set automatically)</p>
                                <Button type="submit" disabled={isSubmitting} className="w-full">
                                  {isSubmitting ? "Saving..." : isEditing ? "Update Standard Chartered Method" : "Add Standard Chartered Method"}
                                </Button>
                              </form>
                            </Form>
                          )}

                          {/* Family Bank Form */}
                          {selectedProvider === "familyBank" && (
                            <Form {...familyBankForm}>
                              <form onSubmit={familyBankForm.handleSubmit(onSubmit)} className="space-y-4">
                                <FormField
                                  control={familyBankForm.control}
                                  name="environment"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Environment</FormLabel>
                                      <Select value={field.value as any} onValueChange={(val) => {
                                        field.onChange(val);
                                        familyBankForm.setValue("baseUrl", val === "production" ? "https://api.familybank.co.ke" : "https://sandbox-api.familybank.co.ke");
                                      }}>
                                        <SelectTrigger><SelectValue placeholder="Select environment" /></SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="sandbox">Sandbox</SelectItem>
                                          <SelectItem value="production">Production</SelectItem>
                                        </SelectContent>
                                      </Select>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <FormField control={familyBankForm.control} name="clientId" render={({ field }) => (
                                  <FormItem><FormLabel>Client ID</FormLabel><FormControl><Input placeholder="Client ID from Family Bank Portal" {...field} /></FormControl><FormMessage /></FormItem>
                                )} />
                                <FormField control={familyBankForm.control} name="clientSecret" render={({ field }) => (
                                  <FormItem><FormLabel>Client Secret</FormLabel><FormControl><Input type="password" placeholder="Client Secret" {...field} /></FormControl><FormMessage /></FormItem>
                                )} />
                                <FormField control={familyBankForm.control} name="apiKey" render={({ field }) => (
                                  <FormItem><FormLabel>API Key</FormLabel><FormControl><Input placeholder="API Key" {...field} /></FormControl><FormMessage /></FormItem>
                                )} />
                                <FormField control={familyBankForm.control} name="accountNumber" render={({ field }) => (
                                  <FormItem><FormLabel>Account Number</FormLabel><FormControl><Input placeholder="Family Bank Account Number" {...field} /></FormControl><FormMessage /></FormItem>
                                )} />
                                <FormField control={familyBankForm.control} name="branchCode" render={({ field }) => (
                                  <FormItem><FormLabel>Branch Code (Optional)</FormLabel><FormControl><Input placeholder="Branch Code" {...field} /></FormControl><FormMessage /></FormItem>
                                )} />
                                <p className="text-sm text-gray-600 dark:text-gray-400">Callback URL: https://us-central1-cbsms-da9ce.cloudfunctions.net/familyBankCallback (set automatically)</p>
                                <Button type="submit" disabled={isSubmitting} className="w-full">
                                  {isSubmitting ? "Saving..." : isEditing ? "Update Family Bank Method" : "Add Family Bank Method"}
                                </Button>
                              </form>
                            </Form>
                          )}
                        </>
                      )}
                      {selectedProvider && !selectedSchool && (
                        <p className="text-sm text-red-600 dark:text-red-400">Please select a school first.</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
              )}
                </ContentLoader>
            </div>
          </div>
        </div>
      </SidebarProvider>
    </ErrorBoundary>
  );
};

export default OwnerPayments;